import moment from 'moment';
import { ResultHistory } from '../../../../../services/MedicalResultsService';

export const ANA_SCREEN_CODE = '42254-3';
export const LOIC_SYSTEM = 'http://loinc.org';

export const getObservationValue = (
  observation: fhir.Observation,
): number | string | undefined => {
  return observation.valueQuantity?.value ?? observation.valueString;
};

export const getObservationValueDisplay = (
  observation: fhir.Observation,
): string => {
  let value = '';
  const valueQuantity =
    observation.valueQuantity || observation.component?.[0]?.valueQuantity;
  if (valueQuantity && valueQuantity.value) {
    const numericValue = Math.round(valueQuantity.value * 100) / 100;
    const comparator = valueQuantity.comparator ?? '';
    const formattedComparator = comparator === 'ad' ? 'ad ' : comparator;
    value = `${formattedComparator}${numericValue}`;
  } else if (observation.valueString) {
    value = observation.valueString;
  } else if (observation.valueCodeableConcept) {
    value =
      observation.valueCodeableConcept.text ??
      observation.valueCodeableConcept.coding?.[0]?.display ??
      '';
  }
  return value.replace('<=', '≤').replace('>=', '≥');
};

export const getObservationDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('LL');
  }
  return undefined;
};

export const getObservationMMMYYYYDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('MMM YYYY');
  }
  return undefined;
};

export const getObservationHHmmDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('HH:mm A');
  }
  return undefined;
};

export const getObservationUnit = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.unit?.trim() ||
    observation.component?.[0]?.valueQuantity?.unit?.trim() ||
    ''
  );
};

export const getObservationUnitCode = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.code?.trim() ||
    observation.component?.[0]?.valueQuantity?.code?.trim() ||
    ''
  );
};

export const getObservationUnitSystem = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.system ||
    observation.component?.[0]?.valueQuantity?.system ||
    ''
  );
};

const UNIT_MAP: Record<string, string[]> = {
  '%': ['%', '% of total Hgb'],
  'uIU/mL': ['uIU/mL', 'uU/L'],
  'mg/dL': ['mg/dL (calc)', 'mg/dL'],
};

export const areUnitsEqual = (
  observation1: fhir.Observation,
  observation2: fhir.Observation,
) => {
  const [code1, system1] = [
    getObservationUnitCode(observation1),
    getObservationUnitSystem(observation1),
  ];
  const [code2, system2] = [
    getObservationUnitCode(observation2),
    getObservationUnitSystem(observation2),
  ];

  const hasCoding = [code1, system1, code2, system2].every(Boolean);

  if (hasCoding && code1 === code2 && system1 === system2) {
    return true;
  }

  const unit1 = getObservationUnit(observation1);
  const unit2 = getObservationUnit(observation2);

  if (unit1 === unit2) {
    return true;
  }

  if (UNIT_MAP[unit1]?.includes(unit2) || UNIT_MAP[unit2]?.includes(unit1)) {
    return true;
  }

  return false;
};

export const getObservationNumericValue = (
  observation: fhir.Observation,
): number | null => {
  if (observation?.valueQuantity?.value !== undefined) {
    return observation.valueQuantity.value;
  }
  if (observation?.component?.length && observation.component.length > 0) {
    return (
      getObservationNumericValue(
        observation.component[0] as fhir.Observation,
      ) ?? null
    );
  }
  // Sometimes observations are beyond the physical detection limit of the labs
  // in those cases, a valueString will be given (ex: '<3'). This returns the number from the value string
  if (
    observation.valueString &&
    (observation.valueString.startsWith('<') ||
      observation.valueString.startsWith('>'))
  ) {
    const value = parseFloat(observation.valueString.slice(1));
    if (isNaN(value)) return null;
    return value;
  }

  return null;
};

export const hasCoding = ({
  observation,
  code,
  system,
}: {
  observation: fhir.Observation;
  code: string;
  system: string;
}) =>
  observation.code?.coding?.some(
    coding => coding.code === code && coding.system === system,
  );

export const groupMedicalResultsByUnit = (
  medicalResults: ResultHistory[],
): Record<string, ResultHistory[]> => {
  const groupedResults: ResultHistory[][] = [];

  medicalResults.forEach(result => {
    const index = groupedResults.findIndex(r =>
      r.some(r => areUnitsEqual(r.resource, result.resource)),
    );
    if (index === -1) {
      groupedResults.push([result]);
    } else {
      groupedResults[index].push(result);
    }
  });

  const resultsByUnit: Record<string, ResultHistory[]> = {};

  groupedResults.forEach(results => {
    const resultWithUnit =
      results.find(r => {
        const unit = getObservationUnit(r.resource);
        return unit !== undefined && unit !== '';
      }) ?? results[0];
    const unit = getObservationUnit(resultWithUnit.resource);
    resultsByUnit[unit] = results;
  });

  return resultsByUnit;
};
